import type { ReactElement } from 'react';

export interface AvatarDef {
  id: string;
  name: string;
  color: string;
  bg: string;
  border: string;
  svg: () => ReactElement;
}

const face = (fx: number, fy: number, bodyColor: string) => (
  <>
    <circle cx={fx - 11} cy={fy} r={6} fill="#1a1a2e" />
    <circle cx={fx - 9} cy={fy - 2} r={2.5} fill="white" />
    <circle cx={fx + 11} cy={fy} r={6} fill="#1a1a2e" />
    <circle cx={fx + 13} cy={fy - 2} r={2.5} fill="white" />
    <path d={`M${fx - 12} ${fy + 13} Q${fx} ${fy + 21} ${fx + 12} ${fy + 13}`} stroke="#1a1a2e" strokeWidth="3.5" fill="none" strokeLinecap="round" />
    <ellipse cx={fx - 16} cy={fy + 8} rx={5} ry={3} fill={bodyColor} opacity={0.55} />
    <ellipse cx={fx + 16} cy={fy + 8} rx={5} ry={3} fill={bodyColor} opacity={0.55} />
  </>
);

const arm = (x: number, y: number, color: string) => (
  <rect x={x} y={y} width="15" height="10" rx="5" fill={color} />
);

export const AVATARS: AvatarDef[] = [
  {
    id: 'boxy', name: 'Boxy', color: '#4A9EFF', bg: 'rgba(74,158,255,0.15)', border: 'rgba(74,158,255,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(1, 30, '#4A9EFF')}{arm(64, 30, '#4A9EFF')}
        <rect x="11" y="11" width="58" height="58" rx="12" fill="#4A9EFF" />
        {face(40, 37, '#7dc0ff')}
      </svg>
    ),
  },
  {
    id: 'tri', name: 'Tri', color: '#FF7A2F', bg: 'rgba(255,122,47,0.15)', border: 'rgba(255,122,47,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 55, '#FF7A2F')}{arm(63, 55, '#FF7A2F')}
        <polygon points="40,5 76,74 4,74" fill="#FF7A2F" />
        {face(40, 50, '#ffb080')}
      </svg>
    ),
  },
  {
    id: 'rosie', name: 'Rosie', color: '#FF6B9D', bg: 'rgba(255,107,157,0.15)', border: 'rgba(255,107,157,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 45, '#FF6B9D')}{arm(63, 45, '#FF6B9D')}
        <circle cx="40" cy="44" r="32" fill="#FF6B9D" />
        {face(40, 42, '#ffaac8')}
      </svg>
    ),
  },
  {
    id: 'ovie', name: 'Ovie', color: '#4CAF50', bg: 'rgba(76,175,80,0.15)', border: 'rgba(76,175,80,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 43, '#4CAF50')}{arm(63, 43, '#4CAF50')}
        <ellipse cx="40" cy="46" rx="36" ry="24" fill="#4CAF50" />
        {face(40, 44, '#80c883')}
      </svg>
    ),
  },
  {
    id: 'hexy', name: 'Hexy', color: '#9B59B6', bg: 'rgba(155,89,182,0.15)', border: 'rgba(155,89,182,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(1, 36, '#9B59B6')}{arm(64, 36, '#9B59B6')}
        <polygon points="40,10 66,25 66,55 40,70 14,55 14,25" fill="#9B59B6" />
        {face(40, 38, '#c490d8')}
      </svg>
    ),
  },
  {
    id: 'gem', name: 'Gem', color: '#F5C518', bg: 'rgba(245,197,24,0.15)', border: 'rgba(245,197,24,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 38, '#F5C518')}{arm(63, 38, '#F5C518')}
        <polygon points="40,4 76,40 40,76 4,40" fill="#F5C518" />
        {face(40, 38, '#fbe080')}
      </svg>
    ),
  },
  {
    id: 'hearty', name: 'Hearty', color: '#E74C3C', bg: 'rgba(231,76,60,0.15)', border: 'rgba(231,76,60,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 41, '#E74C3C')}{arm(63, 41, '#E74C3C')}
        <path d="M40,68 C14,55 6,30 20,20 Q30,11 40,26 Q50,11 60,20 C74,30 66,55 40,68 Z" fill="#E74C3C" />
        {face(40, 38, '#f08080')}
      </svg>
    ),
  },
  {
    id: 'blobby', name: 'Blobby', color: '#1ABC9C', bg: 'rgba(26,188,156,0.15)', border: 'rgba(26,188,156,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 37, '#1ABC9C')}{arm(63, 37, '#1ABC9C')}
        <path d="M40,10 C56,8 72,16 74,32 C76,46 70,60 58,66 C50,70 34,72 22,64 C10,56 6,42 8,28 C10,16 24,12 40,10 Z" fill="#1ABC9C" />
        {face(40, 37, '#70d8c4')}
      </svg>
    ),
  },
  {
    id: 'stary', name: 'Stary', color: '#FF6B6B', bg: 'rgba(255,107,107,0.15)', border: 'rgba(255,107,107,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <polygon points="40,7 48,29 71,30 53,44 59,67 40,54 21,67 27,44 9,30 32,29" fill="#FF6B6B" />
        {face(40, 37, '#ffaaaa')}
      </svg>
    ),
  },
  {
    id: 'penni', name: 'Penni', color: '#8BC34A', bg: 'rgba(139,195,74,0.15)', border: 'rgba(139,195,74,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 40, '#8BC34A')}{arm(63, 40, '#8BC34A')}
        <polygon points="40,8 70,31 58,66 22,66 10,31" fill="#8BC34A" />
        {face(40, 42, '#bcd98c')}
      </svg>
    ),
  },
  {
    id: 'sunny', name: 'Sunny', color: '#F39C12', bg: 'rgba(243,156,18,0.15)', border: 'rgba(243,156,18,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        {arm(2, 39, '#F39C12')}{arm(63, 39, '#F39C12')}
        <path d="M8,56 Q8,10 40,10 Q72,10 72,56 L8,56 Z" fill="#F39C12" />
        {face(40, 36, '#f9c860')}
      </svg>
    ),
  },
  {
    id: 'cloudy', name: 'Cloudy', color: '#7986CB', bg: 'rgba(121,134,203,0.15)', border: 'rgba(121,134,203,0.5)',
    svg: () => (
      <svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
        <circle cx="40" cy="52" r="20" fill="#7986CB" />
        <circle cx="24" cy="42" r="16" fill="#7986CB" />
        <circle cx="56" cy="42" r="16" fill="#7986CB" />
        <circle cx="40" cy="32" r="16" fill="#7986CB" />
        <line x1="7" y1="50" x2="-3" y2="42" stroke="#7986CB" strokeWidth="9" strokeLinecap="round" />
        <line x1="73" y1="50" x2="83" y2="42" stroke="#7986CB" strokeWidth="9" strokeLinecap="round" />
        {face(40, 43, '#b0b8e8')}
      </svg>
    ),
  },
];

export function getAvatar(avatarId: string): AvatarDef {
  return AVATARS.find((a) => a.id === avatarId) ?? AVATARS[0];
}
